// TOEFL iBT Mock 5 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure ("no sooner…than", "the more…the
//                       better", mandative subjunctive, "it is high time",
//                       "would rather" + past, perfect participle clauses).
//                       `accept` lists other word orders that are also
//                       grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal appeal that must rebut evidence,
//                       describe consequences and make a request with an offer.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       AND say who should pay and whether that is fair.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "When did the fire alarm go off during the lecture?",
    prefix: "No sooner",
    answer: ["had", "the", "professor", "started", "speaking", "than", "the", "alarm", "went", "off"],
    suffix: ".",
    distractors: ["when", "has"],
  },
  {
    id: 2,
    speaker: "Is it really worth doing the optional readings?",
    prefix: "Definitely.",
    answer: ["the", "more", "you", "read", "the", "better", "you", "will", "understand", "the", "lectures"],
    suffix: ".",
    distractors: ["most", "best"],
  },
  {
    id: 3,
    speaker: "What did the advisor say about Mark's thesis?",
    prefix: "She insisted that",
    answer: ["he", "submit", "a", "revised", "draft", "by", "the", "end", "of", "the", "month"],
    suffix: ".",
    distractors: ["submitting", "for"],
  },
  {
    id: 4,
    speaker: "Why did you take such an early train this morning?",
    prefix: "I left early",
    answer: ["so", "as", "not", "to", "miss", "the", "opening", "remarks"],
    suffix: ".",
    distractors: ["for", "missing"],
  },
  {
    id: 5,
    speaker: "How was the guest lecture on climate policy?",
    prefix: "Not only",
    answer: ["did", "the", "speaker", "explain", "the", "science", "but", "she", "also", "proposed", "solutions"],
    suffix: ".",
    distractors: ["does", "explained"],
  },
  {
    id: 6,
    speaker: "What exactly do you need to finish your project?",
    prefix: "Honestly,",
    answer: ["what", "I", "need", "most", "is", "more", "time", "to", "analyze", "the", "results"],
    accept: [["what", "I", "most", "need", "is", "more", "time", "to", "analyze", "the", "results"]],
    suffix: ".",
    distractors: ["are", "analyzing"],
  },
  {
    id: 7,
    speaker: "Did the hikers know about the storm?",
    prefix: "Yes, but",
    answer: ["despite", "having", "been", "warned", "they", "continued", "toward", "the", "summit"],
    accept: [["they", "continued", "toward", "the", "summit", "despite", "having", "been", "warned"]],
    suffix: ".",
    distractors: ["although", "warning"],
  },
  {
    id: 8,
    speaker: "What did the interviewer ask you?",
    prefix: "She asked me",
    answer: ["where", "I", "had", "gained", "my", "experience", "in", "data", "analysis"],
    suffix: ".",
    distractors: ["did", "have"],
  },
  {
    id: 9,
    speaker: "Should we start planning next year's conference now?",
    prefix: "Yes, I think",
    answer: ["it", "is", "high", "time", "we", "booked", "a", "venue"],
    suffix: ".",
    distractors: ["book", "will"],
  },
  {
    id: 10,
    speaker: "Can I bring my roommate along to the lab tour?",
    prefix: "I'd rather",
    answer: ["you", "didn't", "bring", "anyone", "who", "hasn't", "signed", "the", "safety", "form"],
    suffix: ".",
    distractors: ["don't", "whom"],
  },
];

export const emailTask = {
  recipient: "Ms. Ferreira",
  scenario:
    "You are the president of the university's Debate Society. The Student Union has just cut your club's annual funding by 60 percent, citing a report that shows average attendance of only eight students per meeting. However, the report used figures from the spring semester, when your meetings were moved online for two months because your usual room was being renovated. Write an email to Ms. Ferreira, the Student Union's finance officer.",
  points: [
    "Explain why the attendance figures in the report are misleading.",
    "Describe what the club would lose if the funding cut goes ahead.",
    "Request a specific action from the Student Union, and offer something in return.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["report", "figures", "numbers", "attendance", "spring", "semester", "online", "renovated", "renovation", "renovations", "room", "moved", "temporary", "misleading", "inaccurate", "not representative", "usually", "normally", "average", "eight", "8", "fall", "this semester", "currently"],
    ["lose", "losing", "cancel", "cancelled", "canceled", "tournament", "tournaments", "competition", "competitions", "travel", "fees", "judges", "workshop", "workshops", "members", "students", "opportunity", "opportunities", "afford", "unable", "could not", "couldn't", "impact", "affect"],
    ["request", "ask", "would like", "reconsider", "review", "restore", "reinstate", "postpone", "delay", "updated", "current", "attendance records", "sign-in", "evidence", "in return", "offer", "willing", "happy to", "could provide", "report back", "volunteer", "host", "open to all"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "urban studies",
  professor: "Dr. Mensah",
  question:
    "Several cities around the world have made their buses and trams completely free to ride. Supporters say free transit reduces car traffic and helps low-income residents; critics say the money would be better spent making services faster and more frequent. Should cities make public transportation free? In your post, also explain who should pay for the cost of the service and why that arrangement would be fair.",
  posts: [
    {
      name: "Priya",
      text: "I support free transit. For many low-income families, bus fares are a significant monthly expense, and removing them would give people better access to jobs and education. Fewer cars on the road would also mean less pollution for everyone.",
    },
    {
      name: "Daniel",
      text: "I disagree. Studies of free transit often show that new riders are people who used to walk or cycle, not drivers. If a bus only comes every forty minutes, making it free won't convince anyone to leave their car at home. Reliability matters more than price.",
    },
  ],
  topicKeywords: [
    "transit", "transport", "transportation", "public", "bus", "buses", "tram", "trams", "train", "metro", "free", "fare", "fares",
    "ticket", "tickets", "city", "cities", "car", "cars", "traffic", "drivers", "riders", "passengers", "pollution", "emissions",
    "income", "low-income", "poor", "cost", "costs", "tax", "taxes", "fund", "funding", "frequency", "frequent", "reliable", "reliability",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on making public transportation free",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "should be free", "should not be free", "shouldn't be free", "make it free", "making it free", "free transit", "free public", "remove fares", "removing fares", "keep fares", "keeping fares", "reduced fares", "discount"],
    },
    {
      label: "Explain who should pay for the service and why that is fair",
      keywords: ["pay for", "paid for", "paid by", "should pay", "funded", "fund it", "tax", "taxes", "taxpayers", "charge drivers", "drivers should", "parking fees", "congestion charge", "fuel tax", "employers", "local businesses", "property", "fair", "fairer", "unfair", "fairness", "benefits from", "those who"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
